import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, AlertCircle, CheckCircle2, Database, ChevronRight, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { GlassCard } from '../shared';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

/**
 * Admin card to manage Quran morphology import/processing for production.
 * Provides UI controls for:
 * - Importing QAC morphology data
 * - Rebuilding lemma/root aggregations
 * - Validating morphology data
 */
const MorphologyToolsCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performing, setPerforming] = useState(null); // 'import' | 'rebuild' | 'validate'
  const [confirmDialog, setConfirmDialog] = useState(null); // action name to confirm
  const [logs, setLogs] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await adminApi.morphology?.status?.() || { data: { counts: {}, last_operation: null } };
      setStatus(r.data);
    } catch (e) {
      console.error('morphology status fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    if (!autoRefresh) return;
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [fetchStatus, autoRefresh]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('fr-FR');
    setLogs(prev => [...prev, { timestamp, message, type }].slice(-20)); // Keep last 20
  };

  const handleImport = async () => {
    setPerforming('import');
    setLogs([]);
    addLog('Démarrage de l\'import morphologie QAC...');
    try {
      const r = await adminApi.morphology?.import?.();
      addLog(`✅ Import terminé: ${r.data?.summary || 'succès'}`, 'success');
      toast.success('Import morphologie complété');
      await fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Erreur inconnue';
      addLog(`❌ Erreur: ${msg}`, 'error');
      toast.error(`Import échoué: ${msg}`);
    } finally {
      setPerforming(null);
      setConfirmDialog(null);
    }
  };

  const handleRebuild = async () => {
    setPerforming('rebuild');
    setLogs([]);
    addLog('Reconstruction des lemmas/roots...');
    try {
      const r = await adminApi.morphology?.rebuild?.();
      addLog(`✅ Reconstruction terminée: ${r.data?.summary || 'succès'}`, 'success');
      toast.success('Lemmas et roots reconstruits');
      await fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Erreur inconnue';
      addLog(`❌ Erreur: ${msg}`, 'error');
      toast.error(`Reconstruction échouée: ${msg}`);
    } finally {
      setPerforming(null);
      setConfirmDialog(null);
    }
  };

  const handleValidate = async () => {
    setPerforming('validate');
    setLogs([]);
    addLog('Validation de la morphologie...');
    try {
      const r = await adminApi.morphology?.validate?.();
      const data = r.data || {};
      addLog(`✅ Validation terminée`, 'success');
      addLog(`   Occurrences: ${data.occurrences || 0}`);
      addLog(`   Avec lemma: ${data.with_lemma || 0}`);
      addLog(`   Avec root: ${data.with_root || 0}`);
      addLog(`   Orphelins: ${data.orphans || 0}`);
      toast.success('Validation complétée');
      await fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Erreur inconnue';
      addLog(`❌ Erreur: ${msg}`, 'error');
      toast.error(`Validation échouée: ${msg}`);
    } finally {
      setPerforming(null);
      setConfirmDialog(null);
    }
  };

  const counts = status?.counts || {};
  const lastOp = status?.last_operation;
  const isWorking = !!performing;

  return (
    <GlassCard data-testid="admin-morphology-tools-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-serif text-lg text-text-primary flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Morphologie du Coran (Production)
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Gérez l'import et la reconstruction de la morphologie Quranic Arabic Corpus
            (lemmes, racines, morphologie) pour l'environnement de production.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => { fetchStatus(); }}
          disabled={loading}
          data-testid="admin-morphology-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          { k: 'occurrences', label: 'Occurrences' },
          { k: 'lemmas', label: 'Lemmas' },
          { k: 'roots', label: 'Racines' },
          { k: 'with_definitions', label: 'Avec définitions' },
        ].map((c) => (
          <div
            key={c.k}
            className="rounded-lg border border-border bg-surface-elevated/50 p-2 text-center"
            data-testid={`admin-morphology-count-${c.k}`}
          >
            <div className="text-lg font-serif text-primary">
              {loading ? '…' : (counts[c.k]?.toLocaleString('fr-FR') ?? '0')}
            </div>
            <div className="text-xs text-text-muted">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Last operation info */}
      {lastOp && (
        <div className="mb-4 p-3 rounded-lg border border-border bg-surface-elevated/40 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-primary" />
            <span className="text-text-secondary">Dernière opération</span>
          </div>
          <div className="text-text-primary font-mono text-xs">
            <div>{lastOp.type} — {lastOp.timestamp}</div>
            {lastOp.status === 'ok' && <div className="text-emerald-400">✓ Succès</div>}
            {lastOp.status === 'failed' && <div className="text-rose-400">✗ Échec: {lastOp.error}</div>}
            {lastOp.message && <div className="text-text-muted">{lastOp.message}</div>}
          </div>
        </div>
      )}

      {/* Control buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Button
          onClick={() => setConfirmDialog('import')}
          disabled={isWorking}
          className="bg-primary text-background"
          data-testid="admin-morphology-import-btn"
        >
          {performing === 'import' ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Import en cours…</>
          ) : (
            <><Database className="w-4 h-4 mr-2" />Importer QAC</>
          )}
        </Button>

        <Button
          onClick={() => setConfirmDialog('rebuild')}
          disabled={isWorking}
          className="bg-primary text-background"
          data-testid="admin-morphology-rebuild-btn"
        >
          {performing === 'rebuild' ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reconstruction…</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" />Reconstruire Lemmas/Roots</>
          )}
        </Button>

        <Button
          onClick={() => setConfirmDialog('validate')}
          disabled={isWorking}
          className="bg-primary text-background"
          data-testid="admin-morphology-validate-btn"
        >
          {performing === 'validate' ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Validation…</>
          ) : (
            <><CheckCircle2 className="w-4 h-4 mr-2" />Valider</>
          )}
        </Button>
      </div>

      {/* Logs */}
      {logs.length > 0 && (
        <div
          className="mb-4 p-3 rounded-lg border border-border bg-surface-elevated/40 font-mono text-xs max-h-40 overflow-y-auto"
          data-testid="admin-morphology-logs"
        >
          {logs.map((log, i) => (
            <div
              key={i}
              className={`text-text-secondary ${
                log.type === 'success' ? 'text-emerald-400' : log.type === 'error' ? 'text-rose-400' : 'text-text-secondary'
              }`}
            >
              <span className="text-text-muted">[{log.timestamp}]</span> {log.message}
            </div>
          ))}
        </div>
      )}

      {/* Confirmation dialogs */}
      <AlertDialog open={confirmDialog !== null} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent data-testid={`admin-morphology-confirm-${confirmDialog}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog === 'import' && 'Lancer l\'import morphologie QAC ?'}
              {confirmDialog === 'rebuild' && 'Reconstruire les lemmas/roots ?'}
              {confirmDialog === 'validate' && 'Valider la morphologie ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog === 'import' && (
                'Cette opération importera les données morphologiques du Quranic Arabic Corpus. Peut durer quelques minutes.'
              )}
              {confirmDialog === 'rebuild' && (
                'Cette opération agrégera les lemmas et roots depuis les occurrences. Idempotente.'
              )}
              {confirmDialog === 'validate' && (
                'Cette opération validera la cohérence des données morphologiques et générera un rapport.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isWorking}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog === 'import') handleImport();
                else if (confirmDialog === 'rebuild') handleRebuild();
                else if (confirmDialog === 'validate') handleValidate();
              }}
              disabled={isWorking}
              className="bg-primary text-background"
              data-testid={`admin-morphology-confirm-${confirmDialog}-btn`}
            >
              {isWorking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Help text */}
      <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs text-text-secondary">
        <p className="mb-2">
          <strong>Workflow recommandé:</strong>
        </p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Cliquez sur <strong>Importer QAC</strong> pour charger la morphologie</li>
          <li>Cliquez sur <strong>Reconstruire</strong> pour aggréger lemmas et roots</li>
          <li>Cliquez sur <strong>Valider</strong> pour vérifier la cohérence</li>
          <li>Les définitions se synchronisent automatiquement via l'onglet lexical</li>
        </ol>
      </div>
    </GlassCard>
  );
};

export default MorphologyToolsCard;
